import React from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

const GbUsSplitFlag = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" className="inline-block rounded-[2px] overflow-hidden" style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.1)' }}>
    {/* Left half — Union Jack */}
    <defs>
      <clipPath id="leftHalf"><rect x="0" y="0" width="10" height="14" /></clipPath>
      <clipPath id="rightHalf"><rect x="10" y="0" width="10" height="14" /></clipPath>
    </defs>
    <g clipPath="url(#leftHalf)">
      <rect width="20" height="14" fill="#012169" />
      <path d="M0,0 L20,14 M20,0 L0,14" stroke="#fff" strokeWidth="2.8" />
      <path d="M0,0 L20,14 M20,0 L0,14" stroke="#C8102E" strokeWidth="1.4" />
      <path d="M10,0 V14 M0,7 H20" stroke="#fff" strokeWidth="4" />
      <path d="M10,0 V14 M0,7 H20" stroke="#C8102E" strokeWidth="2.4" />
    </g>
    {/* Right half — Stars & Stripes */}
    <g clipPath="url(#rightHalf)">
      <rect x="10" y="0" width="10" height="14" fill="#fff" />
      {[0,2,4,6,8,10,12].map(i => (
        <rect key={i} x="10" y={i * 1.077} width="10" height="1.077" fill="#B22234" />
      ))}
      <rect x="10" y="0" width="4.5" height="7.5" fill="#3C3B6E" />
      {[1.2, 3.6, 6].map((cx, ci) => [1.5, 3.5, 5.5].map((cy, ri) => (
        <circle key={`${ci}-${ri}`} cx={cx + 10} cy={cy} r="0.45" fill="#fff" />
      )))}
      {[2.4, 4.8].map((cx, ci) => [2.5, 4.5].map((cy, ri) => (
        <circle key={`o${ci}-${ri}`} cx={cx + 10} cy={cy} r="0.45" fill="#fff" />
      )))}
    </g>
    {/* Center divider */}
    <line x1="10" y1="0" x2="10" y2="14" stroke="rgba(0,0,0,0.15)" strokeWidth="0.3" />
  </svg>
);

const languages = [
  { code: "en", label: "English", flag: null, customFlag: true },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "bn", label: "বাংলা", flag: "🇧🇩" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "pt", label: "Português", flag: "🇧🇷" },
  { code: "ur", label: "اردو", flag: "🇵🇰" },
  { code: "id", label: "Bahasa Indonesia", flag: "🇮🇩" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "pcm", label: "Naijá", flag: "🇳🇬" },
  { code: "mr", label: "मराठी", flag: "🇮🇳" },
  { code: "te", label: "తెలుగు", flag: "🇮🇳" },
  { code: "tr", label: "Türkçe", flag: "🇹🇷" },
  { code: "ta", label: "தமிழ்", flag: "🇮🇳" },
  { code: "yue", label: "粵語", flag: "🇭🇰" },
  { code: "vi", label: "Tiếng Việt", flag: "🇻🇳" },
  { code: "tl", label: "Tagalog", flag: "🇵🇭" },
  { code: "wuu", label: "吴语", flag: "🇨🇳" },
  { code: "ko", label: "한국어", flag: "🇰🇷" },
  { code: "fa", label: "فارسی", flag: "🇮🇷" },
  { code: "ha", label: "Hausa", flag: "🇳🇬" },
  { code: "sw", label: "Kiswahili", flag: "🇰🇪" },
  { code: "jv", label: "Basa Jawa", flag: "🇮🇩" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "uk", label: "Українська", flag: "🇺🇦" },
  { code: "pa", label: "ਪੰਜਾਬੀ", flag: "🇮🇳" },
  { code: "gu", label: "ગુજરાતી", flag: "🇮🇳" },
  { code: "fi", label: "Suomi", flag: "🇫🇮" },
];

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-sky-600 hover:text-sky-800 hover:bg-sky-100"
          data-testid="language-switcher-btn"
          title={currentLang.label}
        >
          <Globe className="w-5 h-5" strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px] max-h-[360px] overflow-y-auto">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => i18n.changeLanguage(lang.code)}
            className={`cursor-pointer ${i18n.language === lang.code ? "bg-sky-50 font-medium" : ""}`}
            data-testid={`lang-option-${lang.code}`}
          >
            <span className="mr-2 text-sm">{lang.customFlag ? <GbUsSplitFlag /> : lang.flag}</span>
            <span className="text-sm">{lang.label}</span>
            {i18n.language === lang.code && (
              <span className="ml-auto text-sky-500 text-xs">&#10003;</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
